<template>
  <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" @click.self="close">
    <div class="w-full max-w-2xl rounded-[16px] bg-white shadow-2xl">
      <div class="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
        <div>
          <h3 class="text-[22px] font-semibold text-[#13161b]">Parametrizar Meta</h3>
          <p class="mt-1 text-sm text-slate-500">
            {{ goal?.title || goal?.metricDisplayName || goal?.metricName || 'Meta CRM' }}
          </p>
        </div>

        <button
          type="button"
          class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
          @click="close">
          <i class="fa-solid fa-xmark" />
        </button>
      </div>

      <div class="space-y-5 px-5 py-5">
        <div>
          <div class="mb-3 text-sm font-semibold text-[#13161b]">Tipo de rentabilidade</div>
          <div class="grid gap-3 md:grid-cols-3">
            <label
              v-for="option in profitabilityOptions"
              :key="option.value"
              class="flex cursor-pointer items-start gap-3 rounded-[12px] border px-4 py-3 transition"
              :class="form.profitabilityScope === option.value ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white hover:border-slate-300'">
              <input v-model="form.profitabilityScope" type="radio" :value="option.value" class="mt-1 h-4 w-4" />
              <div>
                <div class="text-sm font-semibold text-[#13161b]">{{ option.label }}</div>
                <div class="mt-1 text-xs text-slate-500">{{ option.description }}</div>
              </div>
            </label>
          </div>
        </div>

        <div>
          <div class="mb-3 text-sm font-semibold text-[#13161b]">Classificações consideradas</div>
          <div class="grid gap-3 md:grid-cols-2">
            <label
              v-for="option in classificationModeOptions"
              :key="option.value"
              class="flex cursor-pointer items-start gap-3 rounded-[12px] border px-4 py-3 transition"
              :class="form.classificationMode === option.value ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white hover:border-slate-300'">
              <input v-model="form.classificationMode" type="radio" :value="option.value" class="mt-1 h-4 w-4" />
              <div>
                <div class="text-sm font-semibold text-[#13161b]">{{ option.label }}</div>
                <div class="mt-1 text-xs text-slate-500">{{ option.description }}</div>
              </div>
            </label>
          </div>
        </div>

        <div v-if="form.classificationMode === 'selected'" class="rounded-[12px] border border-slate-200 bg-slate-50 p-4">
          <div class="mb-3 text-sm font-semibold text-[#13161b]">Selecione as classificações</div>
          <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <label
              v-for="option in classificationOptions"
              :key="option.value"
              class="flex cursor-pointer items-center gap-3 rounded-[10px] border border-slate-200 bg-white px-3 py-3 transition hover:border-slate-300">
              <input
                :checked="form.classificationValues.includes(option.value)"
                type="checkbox"
                class="h-4 w-4 rounded border-slate-300"
                @change="toggleClassification(option.value, $event.target.checked)" />
              <span class="text-sm font-medium text-slate-700">{{ option.label }}</span>
            </label>
          </div>

          <div v-if="errorMessage" class="mt-3 text-sm font-medium text-rose-600">
            {{ errorMessage }}
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4">
        <Button light label="Cancelar" icon="fa-solid fa-xmark" :disabled="saving" @click="close" />
        <Button primary label="Salvar parametrização" icon="fa-solid fa-floppy-disk" :loader="saving" @click="save" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  goal: { type: Object, default: null },
  saving: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'save'])

const profitabilityOptions = [
  { value: 'total', label: 'Total', description: 'Considera toda a rentabilidade do período da meta.' },
  { value: 'product', label: 'Produto', description: 'Conta apenas pedidos classificados como produto.' },
  { value: 'service', label: 'Serviço', description: 'Conta apenas pedidos classificados como serviço.' },
]

const classificationModeOptions = [
  { value: 'all', label: 'Todas', description: 'Conta qualquer classificação do pedido.' },
  { value: 'selected', label: 'Selecionadas', description: 'Conta apenas as classificações marcadas abaixo.' },
]

const classificationOptions = [
  { value: 'new', label: 'Nova' },
  { value: 'addition', label: 'Adição' },
  { value: 'renewal', label: 'Renovação' },
  { value: 'recurrence', label: 'Recorrência' },
  { value: 'others', label: 'Outro' },
]

const form = ref({
  profitabilityScope: 'total',
  classificationMode: 'all',
  classificationValues: [],
})

const errorMessage = ref('')

const syncForm = () => {
  form.value = {
    profitabilityScope: props.goal?.profitabilityScope || 'total',
    classificationMode: props.goal?.classificationMode || 'all',
    classificationValues: Array.isArray(props.goal?.classificationValues) ? [...props.goal.classificationValues] : [],
  }
  errorMessage.value = ''
}

const close = () => {
  emit('update:modelValue', false)
}

const toggleClassification = (value, checked) => {
  const current = new Set(form.value.classificationValues || [])
  if (checked) current.add(value)
  else current.delete(value)
  form.value.classificationValues = [...current]
}

const save = () => {
  if (form.value.classificationMode === 'selected' && (!form.value.classificationValues || form.value.classificationValues.length === 0)) {
    errorMessage.value = 'Selecione ao menos uma classificação.'
    return
  }

  errorMessage.value = ''
  emit('save', {
    profitabilityScope: form.value.profitabilityScope,
    classificationMode: form.value.classificationMode,
    classificationValues: form.value.classificationMode === 'all' ? [] : form.value.classificationValues,
  })
}

watch(() => props.modelValue, (value) => {
  if (value) syncForm()
})

watch(() => props.goal, () => {
  if (props.modelValue) syncForm()
})
</script>
